<script>
    import {_, locale, number} from 'svelte-i18n';
    import Ribbon from '../lib/Ribbon.svelte';
    import Starburst from '../lib/Starburst.svelte';
    import Countdown from '../lib/Countdown.svelte';
    import {gameConfig, selectedCurrency, user, scrollToPackId, subscriptions, endExpiredSession, isSessionExpired} from '../stores';
    import {getStorePackConfig} from '../yaml';
    import {amazon_pack_image_url, collect_daily_subscription_route, ENABLE_PACK_PREVIEW_SCROLL} from '../constants';
    import ItemsListing from './ItemsListing.svelte';
    import {getPackRarity, resourceDetails} from '../globals';
    import {isQuote} from './pack-pricing.js';
    import {tagNetworkFailure, tagBodyReadFailure} from './network-failure.js';
    import SnowDecoration from "./SnowDecoration.svelte";

    let { subscription = {}, storeSection = '', isPurchased = {}, globals = {}, onbuyPack, compact = false, onOpenDetail } = $props();

    let packDetails = $state(null);

    let processing = $state(false);
    let animate = $state(false);
    let packEl = $state(null);
    // svelte-ignore state_referenced_locally
    let collected = $state(subscription.collected);

    let packIdentifier = $derived(subscription.internalId ?? subscription.packId);

    $effect(() => {
        if ($scrollToPackId === packIdentifier && packEl) {
            scrollToPackId.set(null);
            setTimeout(() => {
                packEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => {
                    animate = true;
                    setTimeout(() => { animate = false; }, 1000);
                }, 500);
            }, 0);
        }
    });
    let rarity = $derived(subscription.rarity);
    let goldBonus = $derived(subscription.goldBonus || 0);
    let goldWorth = $derived(subscription.goldWorth || 0);
    let goldAmount = $derived(storeSection === 'subscriptions' ? isPurchased ? subscription.rewards.gold || 0 : subscription.dailyRewards.gold || 0
        : isPurchased ? subscription.rewards.gold || 0 : subscription.gold || 0);

    let days = $derived(isPurchased ? subscription.rewards.days : subscription.dailyRewards.days);

    let items = $derived(isPurchased
        ? subscription.rewards.items || []
        : subscription.dailyRewards.items || []);

    function formatTimeFriendly(seconds) {
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);

        let timeString = `${minutes} ${$_('Minute')}`;

        if (seconds - 1 > 60 * 60 * 24) {
            timeString = `${days} ${$_('Days')}`;
        } else if (seconds > 60 * 60) {
            timeString = `${hours} ${$_('Hours')}`;
        } else if (seconds >= 60) {
            timeString = `${minutes} ${$_('Minutes')}`;
        }

        return timeString;
    }

    // svelte-ignore state_referenced_locally
    globals.resourceDetails = resourceDetails;
    // svelte-ignore state_referenced_locally
    globals.formatTimeFriendly = formatTimeFriendly;

    $effect(() => {
        globals.goldItemAmount = storeSection === 'subscriptions' ? days : 1;
    });

    async function collectDailySubscription() {
        // Flip to the collected state immediately on click, without waiting for the
        // backend response. `collected` is local reactive state because the subscription
        // prop comes from the store as a plain object — mutating subscription.collected
        // wouldn't re-evaluate the {#if} that swaps in the Collected button (the pattern
        // used in FreePack.svelte).
        processing = true;
        animate = true;
        collected = true;
        // Mirror the collected flag into the store so reactive consumers (e.g. the
        // tab notification dot) recompute. Matches the array element by reference —
        // the prop is the same object held in the subscriptions store.
        subscriptions.update((list) => list.map((s) => (s === subscription ? { ...s, collected: true } : s)));

        try {
            const response = await fetch(collect_daily_subscription_route, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    packId: subscription.packId,
                }),
            }).catch(tagNetworkFailure);

            // web-shop's 403 is the LoggedInGuard: the session is gone, so nothing was collected.
            // Undo the optimistic flag in the store (it outlives this card) and clear the session
            // so <Login/> takes over (see FreePack.svelte).
            if (isSessionExpired(response)) {
                undoOptimisticCollect();
                endExpiredSession(collect_daily_subscription_route);
                return;
            }

            if (!response.ok) {
                throw new Error(`Collect subscription failed: ${response.status}`);
            }

            await response.text().catch(tagBodyReadFailure);

        } catch (error) {
            // Nothing was collected as far as we know: give the player the Collect button back
            // instead of a "Collected" for a reward they never got, and report it as FreePack does.
            undoOptimisticCollect();
            if (error?.isNetworkFailure) {
                Rollbar.info('Collect subscription network failure: ' + error);
            } else {
                Rollbar.error('Error collecting daily subscription: ' + error);
            }
        } finally {
            processing = false;
        }
    }

    // The optimistic update replaced the store element, so match it by packId, not reference.
    function undoOptimisticCollect() {
        animate = false;
        collected = false;
        subscriptions.update((list) => list.map((s) => (s.packId === subscription.packId ? { ...s, collected: false } : s)));
    }

    async function getSubscriptionPackDetails() {
        let details = {};

        await getUnityPackConfig().then((config) => {
            if (config) {
                details.title = config['specialTitleKey'];
                details.description = config['specialTitleDescriptionKey'];

                if (storeSection === 'subscriptions') {
                    details.showOneTimeOnly = config['showOneTimeOnly'];
                    details.showTimer = config['showTimer'];
                }
            } else {
                details.title = subscription['specialTitle'];
            }
        });

        return details;
    }

    function getUnityPackConfig() {
        if (subscription.bundleName !== undefined) {
            return getStorePackConfig(
                amazon_pack_image_url + '/' + subscription.bundleName + '/config.asset',
            );
        }
    }


    async function displayStoreSection() {
        // Snapshot $derived values synchronously before any await — the pack config fetch
        // can outlive the component (the user switches tab while the S3 config.asset
        // request is still in flight), and Svelte 5 inertizes derived state on destroy,
        // returning a Symbol sentinel that crashes the arithmetic below with
        // "Cannot convert a Symbol value to a number" (Rollbar #2509 / #2439).
        // Same guard as StorePack.displayStoreSection().
        const snapDays = days;
        const snapGoldAmount = goldAmount;
        const snapGoldWorth = goldWorth;

        await getSubscriptionPackDetails().then((data) => {
            packDetails = data;

            packDetails.title = $_(data.title);
            packDetails.subTitle = $_(data.title);

            if (storeSection === 'subscriptions') {
                packDetails.description = $_('SubscriptionDescription', {
                    values: {
                        DAYS: '<b>' + snapDays + '</b>',
                        GOLD_VALUE1: '<b>' + $number(snapGoldAmount * snapDays) + '</b>',
                        GOLD_VALUE2: '<b>' + $number(snapGoldWorth) + '</b>',
                    },
                });
            } else {
                packDetails.description = $_(data.description);
            }
        });
    }

    function buyPack() {
        onbuyPack?.({
            storePack: subscription,
            packDetails: packDetails,
        });
    }

    $effect(() => {
        $locale;
        displayStoreSection();
    });

    let packRarity = $derived(getPackRarity(rarity));

    // console.log(subscription);

    function getBonus() {
        if (storeSection === 'subscriptions') {
            if (subscription.showBonus) {
                return $number(Math.floor((goldWorth + goldAmount * days + goldBonus) / (goldAmount * days + goldBonus)) * 100);
            } else {
                if (subscription.discountValue !== undefined) {
                    return `-${subscription.discountValue}`;
                }
            }
        }
    }

    let subscriptionTotalItems = $derived(items !== [] ? items.map(item => {
        return {
            ...item,
            amount: item.amount * days
        }
    }) : []);

    // Compact-card support (mobile). subFullPriceString is also the desktop buy button's
    // strike-through: get-packs leaves paymentHub.fullPrice out when PaymentHub did not quote it.
    let subPriceString = $derived(
        subscription.paymentHub?.price && subscription.paymentHub.price.amount !== 'missing' && subscription.paymentHub.price.currency !== 'missing'
            ? $number(subscription.paymentHub.price.amount / 100, { style: 'currency', currency: subscription.paymentHub.price.currency })
            : ''
    );
    let hasSubDiscount = $derived(
        isQuote(subscription.paymentHub?.fullPrice) && isQuote(subscription.paymentHub?.price)
        && subscription.paymentHub.fullPrice.amount !== subscription.paymentHub.price.amount
    );
    let subFullPriceString = $derived(hasSubDiscount
        ? $number(subscription.paymentHub.fullPrice.amount / 100, { style: 'currency', currency: subscription.paymentHub.fullPrice.currency })
        : '');
    let resourceImageSrc = $derived(`${amazon_pack_image_url}/${subscription.bundleName}/image.jpg`);
    let rarityCls = $derived(storeSection === 'subscriptions' ? (packRarity || 'rare') : '');

    function openDetail() {
        onOpenDetail?.({
            storePack: subscription,
            storeSection,
            packDetails,
            items: storeSection === 'subscriptions' ? subscriptionTotalItems : items,
            goldAmount,
            goldWorth,
            priceString: subPriceString,
            fullPriceString: subFullPriceString,
            imageSrc: storeSection === 'resources' ? resourceImageSrc : '',
            globals,
            rarityClass: rarityCls,
            isPurchased,
            // Same badges the card shows: the bonus starburst and the -10%
            // ribbon on the buy plate.
            badge: storeSection === 'subscriptions' && subscription.showBonus
                ? { label: 'Bonus', text: getBonus() + '%' }
                : null,
            discountBadgeSrc: isPurchased ? '' : '/images/lucky_draw/badge/red_discount_10.png',
        });
    }
</script>

{#if $gameConfig?.gameItems}
    {#await $gameConfig.gameItems then gameItems}
        {#if packDetails}
            {#if compact}
            <!-- role + tabindex are set together: when focusable (tabindex 0) the
                 card is always role="button" with key handlers, so it's a genuine
                 interactive element. Svelte can't see that through the ternaries. -->
            <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
            <div class="pack-compact {rarityCls} {storeSection} {storeSection === 'subscriptions' ? 'noart' : ''}"
                 role={isPurchased ? null : 'button'}
                 tabindex={isPurchased ? null : 0}
                 onclick={isPurchased ? null : openDetail}
                 onkeydown={isPurchased ? null : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(); } }}>
                {#if packDetails.showOneTimeOnly}
                    <Ribbon>{$_('OneTimeOnlyPromoText')}</Ribbon>
                {/if}
                {#if storeSection === 'subscriptions' && subscription.showBonus}
                    <Starburst>{getBonus()}%</Starburst>
                {/if}
                {#if storeSection === 'resources'}
                    <!-- Same layout as the promos packs: title on the art, items
                         in the body strip below the rewards (not over the art). -->
                    <div class="pc-art">
                        <img src={resourceImageSrc} alt={$_(packDetails.title)} loading="lazy"/>
                        <div class="pc-arttitle">
                            <h3 class="pc-title">{$_(packDetails.subTitle)}</h3>
                        </div>
                    </div>
                {:else}
                    <div class="pc-banner">
                        <h3 class="pc-title">{$_(packDetails.subTitle)}</h3>
                    </div>
                {/if}
                {#if goldAmount > 0}
                    <!-- Resources cards use the vibrant reward band like the promos
                         packs; subscriptions keep the plain rewards on the body. -->
                    <div class="pc-rewards {storeSection === 'resources' ? 'pc-rewards-band' : ''}">
                        <span class="pc-rewards-gold">{$_('PacksReceiveGold')} {$number(goldAmount * (days || 1))} {$_('Gold')}</span>
                        {#if goldWorth > 0}
                            <span class="pc-rewards-worth">{@html $_('PacksDescription', { values: { GOLD_VALUE: $number(goldWorth) } })}</span>
                        {/if}
                    </div>
                {/if}
                <!-- Items strip on the card body between the rewards and the timer /
                     button, matching the paid packs' layout (was a dark substrip
                     right under the title). -->
                <div class="pc-items">
                    <ItemsListing globals={globals} goldAmount={goldAmount} items={storeSection === 'subscriptions' ? subscriptionTotalItems : items} storePack={{}} hasMask={false} scrollX={ENABLE_PACK_PREVIEW_SCROLL}/>
                </div>
                {#if isPurchased}
                    {#if collected}
                        <button class="button primary claim-button green pc-collect" disabled>{$_('Collected')}</button>
                    {:else}
                        {#if subscription.finishAt || subscription.endAt}
                            <Countdown endDate={subscription.finishAt ?? subscription.endAt}>
                                {$_('IncrementalPacksTimeLeft')}
                            </Countdown>
                        {/if}
                        <button class="button primary claim-button green pc-collect" class:loading={processing} onclick={(e) => { e.stopPropagation(); collectDailySubscription(); }}>{$_('Collect')}</button>
                    {/if}
                {:else}
                    {#if subscription.finishAt}
                        <Countdown endDate={subscription.finishAt}>
                            {$_('IncrementalPacksTimeLeft')}
                        </Countdown>
                    {/if}
                    <div class="pc-body">
                        <img class="pc-discount-badge" alt="Discount" src="/images/lucky_draw/badge/red_discount_10.png"/>
                        <span class="pc-prices">
                            {#if subFullPriceString}<s class="pc-was">{subFullPriceString}</s>{/if}
                            <span class="pc-price">{subPriceString}</span>
                        </span>
                    </div>
                {/if}
            </div>
            {:else}
            <div bind:this={packEl} id="pack-{packIdentifier}" class="pack {storeSection === 'subscriptions' ? packRarity: ''} {storeSection} {collected ? '': 'active'} promos animate__animated {animate ? 'animate__rubberBand' : ''}">
                {#if packDetails.showOneTimeOnly}
                    <Ribbon>{$_('OneTimeOnlyPromoText')}</Ribbon>
                {/if}
                {#if storeSection === 'subscriptions' && subscription.showBonus}
                    <Starburst>
                        {getBonus()}%
                    </Starburst>
                {/if}
                <div class="content">
                    {#if storeSection === "resources"}
                        <div class="header">
                            <span class="shadow"></span>
                            <picture>
                                <source srcset="{amazon_pack_image_url}/{subscription.bundleName}/image.webp"
                                        type="image/webp">
                                <source srcset="{amazon_pack_image_url}/{subscription.bundleName}/image.jpg"
                                        type="image/jpeg">
                                <img
                                        class="pack-image"
                                        src="{amazon_pack_image_url}/{subscription.bundleName}/image.jpg"
                                        alt={$_(packDetails.title)}
                                />
                            </picture>
                        </div>
                    {/if}

                    <div class="sub-header">
                        <h3>{$_(packDetails.subTitle)}</h3>

                        {#if storeSection === "resources" && goldAmount}
                            <div class="price">
                                <span class="gold">{$_("PacksReceiveGold")} {$number(goldAmount)} {$_("Gold")}</span>
                            </div>
                        {/if}
                        <p>
                            {@html $_(packDetails.description)}
                        </p>
                    </div>

                    {#if storeSection === "subscriptions"}
                        <ItemsListing globals={globals} goldAmount={goldAmount} items={subscriptionTotalItems}
                                      storePack={{}}/>
                    {/if}
                </div>

                {#if storeSection === 'resources'}
                    <div class="button-background"></div>
                {/if}

                {#if isPurchased}
                    {#if collected}
                        <span class="timer">{$_('SubscriptionPackCollectPopupInfo')}</span>
                        <button class="button primary claim-button green" disabled>
                            {$_('Collected')}
                        </button>
                    {:else}
                        {#if storeSection === 'subscriptions' && subscription.finishAt}
                            <Countdown endDate={subscription.finishAt}>
                                {$_('IncrementalPacksTimeLeft')}
                            </Countdown>
                        {/if}

                        {#if storeSection === 'subscriptions' && subscription.endAt}
                            <Countdown endDate={subscription.endAt}>
                                {$_('IncrementalPacksTimeLeft')}
                            </Countdown>
                        {/if}

                        <button class="button primary claim-button green" class:loading={processing}
                                onclick={collectDailySubscription}>
                            <SnowDecoration/>
                            {$_('Collect')}
                        </button>

                        {#if storeSection === 'resources' && subscription.finishAt}
                            <Countdown endDate={subscription.finishAt}>
                                {$_('IncrementalPacksTimeLeft')}
                            </Countdown>
                        {/if}
                    {/if}
                {:else}
                    {#if subscription.finishAt}
                        <Countdown endDate={subscription.finishAt}>
                            {$_('IncrementalPacksTimeLeft')}
                        </Countdown>
                    {:else}
                        {#if storeSection === 'subscriptions' }
                            {#if subscription.endAt}
                                <Countdown endDate={subscription.endAt}>
                                    {$_('IncrementalPacksTimeLeft')}
                                </Countdown>
                            {:else}
                                <span class="timer"></span>
                            {/if}
                        {/if}
                    {/if}
                    <button class="button primary buy-button orange" onclick={buyPack}>
                        <SnowDecoration/>
                        <img alt="Discount" class="lucky-draw-discount"
                             src="/images/lucky_draw/badge/red_discount_10.png"/>
                        {#if subFullPriceString}<s>{subFullPriceString}</s>{/if}
                        {#if subscription.paymentHub.price.amount !== 'missing' && subscription.paymentHub.price.currency !== 'missing'}
                            {$number(subscription.paymentHub.price.amount / 100, {
                                style: 'currency',
                                currency: subscription.paymentHub.price.currency,
                            })}
                        {:else}
                            Price or currency missing
                        {/if}
                    </button>
                    {#if storeSection === 'resources' || storeSection === 'subscriptions'}
                        <span class="timer validFor">{$_('NewShopValidFor')} {days} {$_('Days')}</span>
                    {/if}
                {/if}
            </div>
            {/if}
        {/if}
    {/await}
{/if}

<style lang="scss">
  @use '../styles/vars.scss' as *;
  @use '../styles/compact-card.scss' as compact;

  :global {

    .pack {

      &.subscriptions {
        height: $store-pack-height-subscriptions;

        .starburst {
          right: -16px;
          top: -28px;
        }

        .sub-header {
          margin-top: -5px;
          height: 100px;
          top: 0;
          overflow: visible;
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
        }

        h3 {
          font-family: var(--font-family-sans-condensed);
          font-size: 17px;
          line-height: 24px;
          font-weight: 700;
          margin: 0;
          color: #feffd6;
        }

        p {
          font-size: 12px;
          font-weight: 400;
          margin: 0;
          padding: 0;
          line-height: 14px;
        }

        .items {
          margin-top: 75px;
        }

        .claim-button.green {
          height: 52px;
        }

        .timer {
          height: 12px;
        }

        .validFor {
          position: relative;
          bottom: 2px;
        }
      }

      &.resources {
        .content {
          height: 220px;
        }

        .sub-header {
          margin-top: -30px;
          height: 220px;
          padding-bottom: 105px;

          h3 {
            margin-top: 5px;
            font-size: 16px;
            line-height: 20px;
          }

          p {
            font-size: 12px;
            line-height: 13px;
            margin-bottom: 5px;
          }

          .gold {
            font-size: 14px;
            text-shadow: 1px 1px 0 rgb(0 0 0 / 40%);
          }
        }

        .timer {
          text-align: center !important;
          display: inline-block;
          font-size: 12px;
          line-height: 12px;
          font-weight: 700;
          color: rgba(0, 0, 0, 0.7);
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
          transition: opacity 0.4s;
          margin-bottom: -12px;
          margin-top: 12px;
          z-index: 15;
        }

        .claim-button.green {
          height: 52px;
        }
      }

    }

    .button-background {
      box-shadow: none;
      background: rgba(0, 0, 0, 0.231372549);
      position: absolute;
      z-index: 15;
      left: -10px;
      margin-top: 208px;
      height: 68px;
      right: -10px;
    }
  }

  @include compact.compact-card-styles;
</style>
